import type { ComputedRef, Ref } from "vue"
import { createContext } from "reka-ui"

export const SIDEBAR_COOKIE_NAME = "sidebar_state"
export const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7
export const SIDEBAR_WIDTH = "16rem"
export const SIDEBAR_WIDTH_MOBILE = "18rem"
export const SIDEBAR_WIDTH_ICON = "3rem"
export const SIDEBAR_KEYBOARD_SHORTCUT = "b"

/**
 * In-memory copy of the desktop sidebar open state.
 *
 * Each page wraps itself in AppLayout (flat Vue Router routes), so SidebarProvider
 * remounts on every navigation. The cookie is written on toggle, but reading it
 * from withDefaults would evaluate once at module load — after a SPA remount the
 * stale default re-opens (or re-collapses) the sidebar. Memory survives remounts
 * in the same JS context; the cookie still restores state after a full reload.
 */
let sidebarOpenMemory: boolean | undefined

export function readSidebarOpenFromCookie(cookieString?: string): boolean {
  const cookie = cookieString ?? (typeof document !== "undefined" ? document.cookie : "")
  const prefix = `${SIDEBAR_COOKIE_NAME}=`
  const part = cookie.split(/;\s*/).find((entry) => entry.startsWith(prefix))
  if (!part) return true
  return part.slice(prefix.length) !== "false"
}

export function writeSidebarOpenCookie(open: boolean): void {
  if (typeof document === "undefined") return
  document.cookie = `${SIDEBAR_COOKIE_NAME}=${open}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`
}

export function getPersistedSidebarOpen(explicitDefault?: boolean): boolean {
  if (sidebarOpenMemory !== undefined) return sidebarOpenMemory
  sidebarOpenMemory = explicitDefault ?? readSidebarOpenFromCookie()
  return sidebarOpenMemory
}

export function persistSidebarOpen(open: boolean): void {
  sidebarOpenMemory = open
  writeSidebarOpenCookie(open)
}

export const [useSidebar, provideSidebarContext] = createContext<{
  state: ComputedRef<"expanded" | "collapsed">
  open: Ref<boolean>
  setOpen: (value: boolean) => void
  isMobile: Ref<boolean>
  openMobile: Ref<boolean>
  setOpenMobile: (value: boolean) => void
  toggleSidebar: () => void
}>("Sidebar")
